import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, ChevronDown, ChevronUp, Sparkles, PartyPopper } from 'lucide-react';
import TourEngine from './TourEngine';

const API = process.env.REACT_APP_BACKEND_URL;

const GettingStartedChecklist = () => {
  const [checklist, setChecklist] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [activeTour, setActiveTour] = useState(null);
  const [showCongrats, setShowCongrats] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/tours/checklist/status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.items?.length > 0) {
          setChecklist(data);
          if (data.completed === data.total && data.total > 0) {
            setShowCongrats(true);
          }
        }
      })
      .catch(() => {});
  }, [token, activeTour]);

  // Auto-dismiss congrats after 8 seconds
  useEffect(() => {
    if (!showCongrats) return;
    const timer = setTimeout(() => setDismissed(true), 8000);
    return () => clearTimeout(timer);
  }, [showCongrats]);

  const startTour = async (item) => {
    try {
      const slug = item.slug;
      const res = await fetch(`${API}/api/tours/${slug}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const tour = await res.json();
        if (tour.page_view) {
          navigate(`/?view=${tour.page_view}&startTour=${slug}`);
        } else {
          setActiveTour(tour);
        }
      }
    } catch { }
  };

  if (!checklist || checklist.total === 0 || dismissed) return null;

  // All done — show congratulations card
  if (showCongrats) {
    return (
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 shadow-sm p-5 mb-6 animate-in fade-in slide-in-from-top-2 duration-500" data-testid="checklist-congrats">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
            <PartyPopper className="h-6 w-6 text-green-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-bold text-green-900">You're all set!</h3>
            <p className="text-sm text-green-700 mt-0.5">You've completed every getting-started tour. You can always replay tours from Help anytime you need a refresher.</p>
          </div>
          <button onClick={() => setDismissed(true)} className="text-green-400 hover:text-green-600 p-1 rounded-lg flex-shrink-0" data-testid="congrats-dismiss">
            <CheckCircle2 className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  }

  const pct = Math.round((checklist.completed / checklist.total) * 100);

  return (
    <>
      <div className="bg-white rounded-xl border border-amber-200 shadow-sm overflow-hidden mb-6" data-testid="getting-started-checklist">
        {/* Header */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-amber-50/50 transition-colors"
          data-testid="checklist-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-amber-600" />
            </div>
            <div className="text-left">
              <h3 className="text-sm font-bold text-stone-800">Getting Started</h3>
              <p className="text-[11px] text-stone-500">{checklist.completed}/{checklist.total} completed</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Progress bar */}
            <div className="w-24 h-1.5 bg-stone-100 rounded-full overflow-hidden hidden sm:block">
              <div className="h-full bg-amber-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs font-bold text-amber-600">{pct}%</span>
            {expanded ? <ChevronUp className="h-4 w-4 text-stone-400" /> : <ChevronDown className="h-4 w-4 text-stone-400" />}
          </div>
        </button>

        {/* Items */}
        {expanded && (
          <div className="px-4 pb-3 space-y-1">
            {checklist.items.map((item, idx) => (
              <button
                key={item.tour_id}
                onClick={() => !item.completed && startTour(item)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-all text-sm ${
                  item.completed
                    ? 'bg-green-50/70 text-green-700'
                    : 'hover:bg-amber-50 text-stone-700 cursor-pointer'
                }`}
                disabled={item.completed}
                data-testid={`checklist-item-${idx}`}
              >
                {item.completed ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                ) : (
                  <Circle className="h-4 w-4 text-stone-300 flex-shrink-0" />
                )}
                <span className={item.completed ? 'line-through opacity-60' : 'font-medium'}>{item.label}</span>
              </button>
            ))}
            <div className="flex justify-end pt-1">
              <button onClick={() => setDismissed(true)} className="text-[10px] text-stone-400 hover:text-stone-600">
                Dismiss
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Active tour overlay */}
      {activeTour && (
        <TourEngine
          tour={activeTour}
          onComplete={() => setActiveTour(null)}
          onSkip={() => setActiveTour(null)}
        />
      )}
    </>
  );
};

export default GettingStartedChecklist;
